import React from "react";
import { useBlendedLearningCalculator } from "../hooks/useBlendedLearningCalculator";
import type { CalculatorInputs } from "../types";
import { SERVICES_INCLUDED_TEXT, BUDGET_DISCLAIMER_TEXT } from "../constants";
import { ChevronRightIcon } from "./icons/ChevronRightIcon";

const initialInputs: CalculatorInputs = {
  totalWorkshopDuration: 3,
  liveSessions: 1,
  instructorVideoShare: 70,
};

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat("en-US").format(num);
};

const Header = () => (
  <header className="flex items-center mb-8">
    <img src="/logo.png" alt="Cubeish Logo" className="h-14" />
  </header>
);

interface InfoCardProps {
  label: string;
  value: string | number;
  loading: boolean;
}

const InfoCard: React.FC<InfoCardProps> = ({ label, value, loading }) => (
  <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg text-center transition-all duration-300 hover:bg-white/20">
    <div className="text-3xl font-bold text-white">
      {loading ? "..." : value}
    </div>
    <div className="text-xs text-indigo-200 uppercase tracking-wider mt-1">
      {label}
    </div>
  </div>
);

interface InputRowProps {
  label: string;
  description: string;
  value: number;
  onChange: (value: number) => void;
  unit: string;
  min?: number;
  max?: number;
  disabled: boolean;
}

const InputRow: React.FC<InputRowProps> = ({
  label,
  description,
  value,
  onChange,
  unit,
  min,
  max,
  disabled,
}) => (
  <div className="grid grid-cols-12 gap-4 items-center py-3">
    <div className="col-span-1 text-center text-teal-500">
      <ChevronRightIcon className="w-6 h-6 mx-auto" />
    </div>
    <div className="col-span-6">
      <div className="font-semibold text-slate-700">{label}</div>
      <div className="text-sm text-slate-500">{description}</div>
    </div>
    <div className="col-span-3">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full p-2 rounded-md border-2 border-slate-300 bg-white text-slate-800 text-center font-bold focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition disabled:opacity-50"
        min={min}
        max={max}
        disabled={disabled}
        aria-label={label}
      />
    </div>
    <div className="col-span-2 text-center text-slate-600 font-medium">
      {unit}
    </div>
  </div>
);

interface DisplayRowProps {
  label: string;
  description: string;
  value: string | number;
  unit: string;
  loading: boolean;
}

const DisplayRow: React.FC<DisplayRowProps> = ({
  label,
  description,
  value,
  unit,
  loading,
}) => (
  <div className="grid grid-cols-12 gap-4 items-center py-3">
    <div className="col-span-1"></div>
    <div className="col-span-6">
      <div className="font-semibold text-slate-700">{label}</div>
      <div className="text-sm text-slate-500">{description}</div>
    </div>
    <div className="col-span-3 text-center font-bold text-lg text-teal-600">
      {loading ? "..." : value}
    </div>
    <div className="col-span-2 text-center text-slate-600 font-medium">
      {unit}
    </div>
  </div>
);

const CalculatorPage = () => {
  const { inputs, results, loading, handleInputChange } =
    useBlendedLearningCalculator(initialInputs);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <Header />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Main Calculator Panel */}
        <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6 border border-slate-200">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">
            Blended Learning Experience
          </h1>
          <p className="text-slate-500 mb-6">
            Adjust the quantities to simulate your project budget.
          </p>

          <div className="space-y-2 divide-y divide-slate-200">
            <InputRow
              label="Total Workshop Duration"
              description="The complete duration of your existing offline training program"
              value={inputs.totalWorkshopDuration}
              onChange={(v) => handleInputChange("totalWorkshopDuration", v)}
              unit="days"
              min={1}
              disabled={loading}
            />
            <InputRow
              label="Live Sessions"
              description="Interactive sessions that will remain live (online or offline)"
              value={inputs.liveSessions}
              onChange={(v) => handleInputChange("liveSessions", v)}
              unit="days"
              min={0}
              disabled={loading}
            />
            <DisplayRow
              label="Content for Digitization"
              description="Training content that will be converted to digital format"
              value={results.contentForDigitization}
              unit="days"
              loading={loading}
            />
            <DisplayRow
              label="Total Digital Course Duration"
              description="The expected learning duration of the final digital outcome"
              value={results.totalDigitalCourseDuration}
              unit="hrs"
              loading={loading}
            />
            <InputRow
              label="Instructor-Led Video Share"
              description="Percentage of content delivered via instructor videos"
              value={inputs.instructorVideoShare}
              onChange={(v) => handleInputChange("instructorVideoShare", v)}
              unit="%"
              min={0}
              max={100}
              disabled={loading}
            />
            <DisplayRow
              label="Interactive Content Share"
              description="Content via hands-on activities and reading materials"
              value={`${results.interactiveShare}%`}
              unit=""
              loading={loading}
            />
            <DisplayRow
              label="Video Content Duration"
              description="Total learning hours from professionally shot videos"
              value={results.videoContentDuration}
              unit="hrs"
              loading={loading}
            />
            <DisplayRow
              label="Interactive Content Duration"
              description="Total learning hours from activities & reading materials"
              value={results.interactiveContentDuration}
              unit="hrs"
              loading={loading}
            />
          </div>
        </div>

        {/* Summary Panel */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl shadow-2xl p-6 text-white">
            <h2 className="text-xl font-bold mb-4 text-center text-indigo-100">
              Total Project Budget
            </h2>
            <div className="text-5xl font-extrabold text-center mb-6">
              {loading ? "..." : formatNumber(results.totalCost)}{" "}
              <span className="text-3xl font-medium align-middle">EGP</span>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <InfoCard
                label="Videos"
                value={results.numberOfVideos}
                loading={loading}
              />
              <InfoCard
                label="Readings"
                value={results.numberOfReadingMaterials}
                loading={loading}
              />
              <InfoCard
                label="Activities"
                value={results.numberOfInteractiveActivities}
                loading={loading}
              />
            </div>
            <p className="text-xs text-yellow-300 mt-4 italic">
              {BUDGET_DISCLAIMER_TEXT}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
            <h3 className="text-lg font-bold mb-3 border-b border-slate-200 pb-2 text-slate-800">
              Services Included:
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              {SERVICES_INCLUDED_TEXT}
            </p>
            {/* <p className="text-xs text-slate-500 mt-4 italic">
              {BUDGET_DISCLAIMER_TEXT}
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculatorPage;
